class GraphicsMotoFrontBackCollection extends HTMLElement {
  connectedCallback() {
    this.view = this.dataset.initialView === 'back' ? 'back' : 'front';
    this.viewButtons = [...this.querySelectorAll('[data-gm-view-button]')];
    this.cards = [...this.querySelectorAll('[data-gm-product-card]')];
    this.carousel = this.querySelector('[data-gm-carousel]');
    this.carouselControls = this.querySelector('[data-gm-carousel-controls]');
    this.previousButton = this.querySelector('[data-gm-carousel-prev]');
    this.nextButton = this.querySelector('[data-gm-carousel-next]');
    this.isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
    this.handleViewportChange = () => this.updateDesktopLayout();
    this.handleCarouselScroll = () => this.updateCarouselControls();

    this.cards.forEach((card) => this.initializeCard(card));
    this.viewButtons.forEach((button) => {
      button.addEventListener('click', () => this.setView(button.dataset.gmViewButton));
    });
    this.setupCarouselControls();
    this.setView(this.view, false);
    this.updateDesktopLayout();
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.handleViewportChange, { passive: true });
    window.addEventListener('load', this.handleViewportChange, { once: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleViewportChange);
      this.resizeObserver.observe(this);
    }
    GraphicsMotoFrontBackCollection.setupHomepageScrollAlignment();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleViewportChange);
    window.removeEventListener('orientationchange', this.handleViewportChange);
    this.carousel?.removeEventListener('scroll', this.handleCarouselScroll);
    this.resizeObserver?.disconnect();
  }

  setupCarouselControls() {
    if (!this.carousel || !this.previousButton || !this.nextButton) return;

    this.previousButton.addEventListener('click', () => this.scrollCarousel(-1));
    this.nextButton.addEventListener('click', () => this.scrollCarousel(1));
    this.carousel.addEventListener('scroll', this.handleCarouselScroll, { passive: true });
  }

  updateDesktopLayout() {
    if (!this.carousel) return;

    const sectionWrapper = this.closest('.shopify-section');
    const sectionBackground = this.closest('.gm-front-back-section');

    if (!this.isDesktop()) {
      this.style.removeProperty('--gm-desktop-section-height');
      this.style.removeProperty('--gm-desktop-card-width');
      this.style.removeProperty('--gm-desktop-media-size');
      this.style.removeProperty('--gm-desktop-gap');
      sectionWrapper?.style.removeProperty('--gm-desktop-section-height');
      sectionBackground?.style.removeProperty('--gm-desktop-section-height');
      this.carousel.classList.remove('is-centered', 'is-carousel');
      if (this.carouselControls) this.carouselControls.hidden = true;
      return;
    }

    const headerHeight =
      Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1280;
    const sectionHeight = Math.max(560, viewportHeight - headerHeight);
    const innerWidth = this.querySelector('.gm-front-back__inner')?.clientWidth || this.clientWidth || viewportWidth;
    const gap = viewportWidth >= 1600 ? 24 : 20;
    const desiredCards = viewportWidth >= 1680 ? 5 : viewportWidth >= 1180 ? 4 : 3;
    const visibleCards = Math.max(3, Math.min(desiredCards, this.cards.length || desiredCards));
    const cardWidth = Math.floor((innerWidth - gap * (visibleCards - 1)) / visibleCards);
    const safeCardWidth = Math.max(190, Math.min(cardWidth, 310));
    const mediaSize = Math.max(190, Math.min(Math.floor(sectionHeight * 0.39), safeCardWidth));
    const contentWidth = this.cards.length * safeCardWidth + Math.max(0, this.cards.length - 1) * gap;
    const shouldCarousel = contentWidth > innerWidth + 2;

    this.style.setProperty('--gm-desktop-section-height', `${sectionHeight}px`);
    this.style.setProperty('--gm-desktop-card-width', `${safeCardWidth}px`);
    this.style.setProperty('--gm-desktop-media-size', `${mediaSize}px`);
    this.style.setProperty('--gm-desktop-gap', `${gap}px`);
    sectionWrapper?.style.setProperty('--gm-desktop-section-height', `${sectionHeight}px`);
    sectionBackground?.style.setProperty('--gm-desktop-section-height', `${sectionHeight}px`);
    this.carousel.classList.toggle('is-centered', !shouldCarousel);
    this.carousel.classList.toggle('is-carousel', shouldCarousel);
    if (this.carouselControls) this.carouselControls.hidden = !shouldCarousel;
    this.updateCarouselControls();
  }

  scrollCarousel(direction) {
    if (!this.carousel) return;

    const distance = Math.max(this.carousel.clientWidth * 0.84, 260);
    this.carousel.scrollBy({
      left: distance * direction,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  updateCarouselControls() {
    if (!this.carousel || !this.previousButton || !this.nextButton || !this.isDesktop()) return;

    const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth;
    const isScrollable = maxScroll > 2;
    if (this.carouselControls) this.carouselControls.hidden = !isScrollable;
    this.previousButton.disabled = this.carousel.scrollLeft <= 2;
    this.nextButton.disabled = this.carousel.scrollLeft >= maxScroll - 2;
  }

  static setupHomepageScrollAlignment() {
    if (window.graphicsMotoSectionAlignmentReady) return;

    const main = document.querySelector('#MainContent[data-template="index"]');
    if (!main) return;

    window.graphicsMotoSectionAlignmentReady = true;
    let settleTimer;
    let isAligning = false;
    let suppressAlignmentUntil = 0;
    let lastScrollY = window.scrollY;
    let scrollDirection = 0;
    let lastUserDirectionAt = 0;

    const alignNearbySection = (direction = 0) => {
      if (isAligning || Date.now() < suppressAlignmentUntil || window.innerWidth < 990) return;

      const sections = [...main.querySelectorAll('.shopify-section')].filter((section) =>
        section.querySelector('.gm-front-back-section')
      );
      if (!sections.length) return;

      const headerHeight =
        Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
      const targetTop = headerHeight;
      const threshold = Math.min(420, window.innerHeight * 0.52);
      const measuredTargets = sections
        .map((target) => ({
          target,
          offset: target.getBoundingClientRect().top - targetTop,
          distance: Math.abs(target.getBoundingClientRect().top - targetTop),
        }))
        .sort((left, right) => left.distance - right.distance);
      const directionalTargets = measuredTargets.filter(({ offset }) =>
        direction > 0 ? offset > 4 : direction < 0 ? offset < -4 : true
      );
      const directionalNearest = directionalTargets[0];
      const nearbyNearest = measuredTargets[0];
      const nearest =
        directionalNearest && directionalNearest.distance <= threshold
          ? directionalNearest
          : nearbyNearest?.distance <= 180
            ? nearbyNearest
            : null;

      if (!nearest || nearest.distance < 4 || nearest.distance > threshold) return;

      const alignmentTop = nearest.target.getBoundingClientRect().top;
      const destination = window.scrollY + alignmentTop - targetTop;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      isAligning = true;
      suppressAlignmentUntil = Date.now() + (reducedMotion ? 0 : 900);
      window.scrollTo({
        top: destination,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
      window.setTimeout(() => {
        isAligning = false;
      }, reducedMotion ? 0 : 900);
    };

    window.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaY) < 1) return;
        scrollDirection = Math.sign(event.deltaY);
        lastUserDirectionAt = Date.now();
      },
      { passive: true }
    );

    window.addEventListener(
      'scroll',
      () => {
        const currentScrollY = window.scrollY;
        if (
          !isAligning &&
          Date.now() >= suppressAlignmentUntil &&
          Date.now() - lastUserDirectionAt > 220 &&
          Math.abs(currentScrollY - lastScrollY) > 1
        ) {
          scrollDirection = Math.sign(currentScrollY - lastScrollY);
        }
        lastScrollY = currentScrollY;
        window.clearTimeout(settleTimer);
        if (Date.now() < suppressAlignmentUntil) return;
        settleTimer = window.setTimeout(() => alignNearbySection(scrollDirection), 140);
      },
      { passive: true }
    );
  }

  initializeCard(card) {
    const dataElement = card.querySelector('[data-gm-view-data]');
    if (!dataElement) return;

    try {
      card.viewData = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.warn('GraphicsMoto product view data could not be parsed.', error);
      return;
    }

    const variantDataElement = card.querySelector('[data-gm-variant-data]');
    if (variantDataElement) {
      try {
        card.variantData = JSON.parse(variantDataElement.textContent);
      } catch (error) {
        console.warn('GraphicsMoto variant data could not be parsed.', error);
      }
    }

    const colors = Object.keys(card.viewData.colors || {}).sort((left, right) => {
      const leftIsBlack = left.toLowerCase() === 'black';
      const rightIsBlack = right.toLowerCase() === 'black';

      if (leftIsBlack === rightIsBlack) return 0;
      return leftIsBlack ? -1 : 1;
    });
    if (!colors.length) return;

    const requestedDefault = card.viewData.defaultColor;
    const blackColor = colors.find((color) => color.toLowerCase() === 'black');
    card.selectedColor = blackColor || (colors.includes(requestedDefault) ? requestedDefault : colors[0]);
    card.dataset.gmHasViewData = 'true';

    const colorControls = card.querySelector('[data-gm-color-controls]');
    const template = card.querySelector('[data-gm-color-button-template]');
    if (colorControls && template) {
      colors.forEach((color) => {
        const button = template.content.firstElementChild.cloneNode(true);
        button.dataset.gmColor = color;
        button.textContent = color;
        button.setAttribute('aria-label', `Show ${color}`);
        button.addEventListener('click', () => this.setCardColor(card, color));
        colorControls.append(button);
      });

      colorControls.hidden = false;
    }

    this.syncColorButtons(card);
    this.initializeSizeControls(card);
    this.syncVariantSelection(card);
  }

  initializeSizeControls(card) {
    const variants = card.variantData?.variants || [];
    const sizeControls = card.querySelector('[data-gm-size-controls]');
    const template = card.querySelector('[data-gm-size-button-template]');
    if (!variants.length || !sizeControls || !template) return;

    const sizes = [...new Set(variants.map((variant) => variant.size).filter(Boolean))];
    if (!sizes.length) return;

    sizes.forEach((size) => {
      const button = template.content.firstElementChild.cloneNode(true);
      button.dataset.gmSize = size;
      button.textContent = size;
      button.setAttribute('aria-label', `Select size ${size}`);
      button.addEventListener('click', () => this.setCardSize(card, size));
      sizeControls.append(button);
    });

    sizeControls.hidden = false;
  }

  setView(view, animate = true) {
    if (view !== 'front' && view !== 'back') return;
    this.view = view;
    this.dataset.currentView = view;

    this.viewButtons.forEach((button) => {
      const selected = button.dataset.gmViewButton === view;
      button.setAttribute('aria-pressed', String(selected));
    });

    this.cards.forEach((card) => this.updateCardImage(card, animate));
  }

  setCardColor(card, color) {
    if (!card.viewData?.colors?.[color]) return;
    card.selectedColor = color;
    this.syncColorButtons(card);
    this.updateCardImage(card, true);
    this.syncVariantSelection(card);
  }

  setCardSize(card, size) {
    const variant = (card.variantData?.variants || []).find(
      (candidate) =>
        candidate.color === card.selectedColor &&
        candidate.size === size &&
        candidate.available
    );
    if (!variant) return;

    card.selectedSize = size;
    this.syncVariantSelection(card);
  }

  syncColorButtons(card) {
    card.querySelectorAll('[data-gm-color]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.gmColor === card.selectedColor));
    });
  }

  syncVariantSelection(card) {
    const variants = card.variantData?.variants || [];
    if (!variants.length) return;

    const colorVariants = variants.filter((variant) => variant.color === card.selectedColor);
    const selectedVariant =
      colorVariants.find((variant) => variant.size === card.selectedSize && variant.available) ||
      colorVariants.find((variant) => variant.available) ||
      colorVariants[0];
    if (!selectedVariant) return;

    card.selectedSize = selectedVariant.size;

    card.querySelectorAll('[data-gm-size]').forEach((button) => {
      const matchingVariant = colorVariants.find(
        (variant) => variant.size === button.dataset.gmSize
      );
      const available = Boolean(matchingVariant?.available);
      button.disabled = !available;
      button.setAttribute(
        'aria-pressed',
        String(available && button.dataset.gmSize === card.selectedSize)
      );
    });

    const price = card.querySelector('[data-gm-product-price]');
    if (price) price.textContent = selectedVariant.price;

    const variantUrl = `${card.dataset.productUrl}?variant=${selectedVariant.id}`;
    card.querySelectorAll('[data-gm-product-link]').forEach((link) => {
      link.href = variantUrl;
    });
  }

  updateCardImage(card, animate) {
    const image = card.querySelector('.gm-product-card__image');
    const views = card.viewData?.colors?.[card.selectedColor];
    const source = views?.[this.view];
    if (!image || !source || image.dataset.gmSource === source) return;

    if (animate) image.classList.add('is-changing');

    const applySource = () => {
      const revealImage = () => requestAnimationFrame(() => image.classList.remove('is-changing'));

      image.addEventListener('load', revealImage, { once: true });
      image.addEventListener('error', revealImage, { once: true });
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
      image.dataset.gmSource = source;
      image.alt = `${card.dataset.productTitle} — ${card.selectedColor} ${this.view}`;
      if (image.complete) revealImage();
    };

    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.setTimeout(applySource, 90);
    } else {
      applySource();
      image.classList.remove('is-changing');
    }
  }
}

if (!customElements.get('gm-front-back-collection')) {
  customElements.define('gm-front-back-collection', GraphicsMotoFrontBackCollection);
}
